import { useCallback, useMemo } from 'react'

import { Select } from 'govwell-ui'
import { SelectOption } from 'govwell-ui/components/Select/types'
import { DateTemplateInput, DateTemplateIntervalInput } from 'types/graphql'

import { DateTemplateRelation } from 'src/models/Settings/Interval'

const Options: SelectOption<DateTemplateRelation>[] = [
  {
    value: DateTemplateRelation.InTheFuture,
    label: 'in the future',
  },
  {
    value: DateTemplateRelation.BeforeExpiration,
    label: 'before expiration',
  },
  {
    value: DateTemplateRelation.AfterExpiration,
    label: 'after expiration',
  },
]

type Props = {
  dateTemplate: Partial<DateTemplateInput>
  isDisabled?: boolean
  onChange: (value: Partial<DateTemplateInput>) => void
  options?: DateTemplateRelation[]
}
const DateTemplateRelationSelect = (props: Props) => {
  const { dateTemplate, isDisabled, onChange } = props
  const options = useMemo(() => {
    return props.options ? props.options.map((o) => ({ value: o, label: o })) : Options
  }, [props.options])
  const selectedOption = useMemo(() => {
    const interval = dateTemplate?.interval ?? {}
    let relation: DateTemplateRelation
    if (interval.relativeToDate === 'Expiration') {
      if (interval.temporalPosition === 'After') {
        relation = DateTemplateRelation.AfterExpiration
      } else {
        relation = DateTemplateRelation.BeforeExpiration
      }
    } else {
      relation = DateTemplateRelation.InTheFuture
    }

    return options.find((o) => o.value === relation)
  }, [dateTemplate?.interval])

  const handleSelectedOptionChange = useCallback(
    (newSelectedOption?: SelectOption<DateTemplateRelation>) => {
      if (!dateTemplate?.interval || !newSelectedOption) {
        return
      }
      const intervalUpdates: DateTemplateIntervalInput = {}
      const value = newSelectedOption.value
      if (value === DateTemplateRelation.AfterExpiration) {
        intervalUpdates.relativeToDate = 'Expiration'
        intervalUpdates.temporalPosition = 'After'
      } else if (value === DateTemplateRelation.BeforeExpiration) {
        intervalUpdates.relativeToDate = 'Expiration'
        intervalUpdates.temporalPosition = 'Before'
      } else {
        intervalUpdates.relativeToDate = 'Now'
        intervalUpdates.temporalPosition = 'After'
      }

      onChange({
        ...dateTemplate,
        interval: {
          ...dateTemplate.interval,
          ...intervalUpdates,
        },
      })
    },
    [dateTemplate, onChange]
  )

  return (
    <Select
      ariaLabel="Delay relation"
      getOptionKey={(o) => o.value}
      isDisabled={isDisabled}
      onSelectedOptionChange={handleSelectedOptionChange}
      options={options}
      placeholder="e.g. in the future"
      selectedOption={selectedOption}
      width="160px"
    />
  )
}

export default React.memo(DateTemplateRelationSelect)
